import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Admin/Login/AuthContext";
import { authFetch } from "../../../../utils/cmsAuth";
import "./ConciergeDashboard.css";

const SETTINGS_FIELDS = [
  "upload_start_time",
  "upload_end_time",
  "upload_end_day_offset",
  "min_upload_interval_minutes",
  "upload_completion_sms_enabled",
  "upload_completion_sms_start_time",
  "upload_completion_sms_end_time",
  "view_threshold_sms_enabled",
  "view_threshold_sms_start_time",
  "view_threshold_sms_end_time",
];
const TIME_FIELDS = new Set(SETTINGS_FIELDS.filter((key) => key.endsWith("_time")));
const STATE_LABELS = { UPLOADING: "업로드 중", WAITING: "대기", STOPPED: "중지", STALLED: "확인 필요" };
const SCHEDULER_STATUS_LABELS = {
  HEALTHY: "정상 응답",
  NO_RESPONSE: "응답 없음",
  CONFIG_MISMATCH: "설정 불일치",
  STOPPED: "중지",
};
const JOB_LABELS = { MANUAL: "수동", IMMEDIATE: "신규 승인", WEEKLY: "주간 자동" };
const SETTING_LABELS = {
  auto_upload_enabled: "자동업로드",
  upload_start_time: "업로드 시작",
  upload_end_time: "업로드 종료",
  upload_end_day_offset: "업로드 종료 기준일",
  min_upload_interval_minutes: "최소 업로드 간격",
  upload_completion_sms_enabled: "업로드 완료 문자",
  upload_completion_sms_start_time: "완료 문자 시작",
  upload_completion_sms_end_time: "완료 문자 종료",
  view_threshold_sms_enabled: "조회수 달성 문자",
  view_threshold_sms_start_time: "조회수 문자 시작",
  view_threshold_sms_end_time: "조회수 문자 종료",
};

function number(value) {
  return Number(value || 0).toLocaleString("ko-KR");
}

function durationMinutes(value) {
  const total = Math.max(0, Number(value || 0));
  if (total < 60) return `${total}분`;
  const hours = Math.floor(total / 60);
  const minutesLeft = total % 60;
  return minutesLeft ? `${hours}시간 ${minutesLeft}분` : `${hours}시간`;
}

function kstTime(value) {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(parsed);
}

function minutes(time) {
  const [hour, minute] = String(time || "00:00").split(":").map(Number);
  return hour * 60 + minute;
}

function validClock(value) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value || ""));
}

function clock(total) {
  const local = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(local / 60)).padStart(2, "0")}:${String(local % 60).padStart(2, "0")}`;
}

function draftFrom(settings) {
  return Object.fromEntries(SETTINGS_FIELDS.map((key) => [
    key, TIME_FIELDS.has(key) ? String(settings[key]).slice(0, 5) : settings[key],
  ]));
}

function changedFields(draft, original) {
  return Object.fromEntries(SETTINGS_FIELDS.filter((key) => {
    const oldValue = TIME_FIELDS.has(key) ? String(original[key]).slice(0, 5) : original[key];
    return draft[key] !== oldValue;
  }).map((key) => [key, draft[key]]));
}

function settingValue(key, value) {
  if (key === "auto_upload_enabled" || key.endsWith("_sms_enabled")) {
    return value ? "ON" : "OFF";
  }
  if (key === "upload_end_day_offset") return value ? "익일" : "당일";
  if (key === "min_upload_interval_minutes") return `${value}분`;
  return String(value ?? "-").slice(0, TIME_FIELDS.has(key) ? 5 : undefined);
}

function auditChanges(row) {
  return Object.keys(SETTING_LABELS).filter((key) =>
    String(row.before?.[key]) !== String(row.after?.[key])
  ).map((key) => `${SETTING_LABELS[key]} ${settingValue(key, row.before?.[key])} → ${settingValue(key, row.after?.[key])}`);
}

async function api(path, options = {}) {
  const response = await authFetch(`/concierge/dashboard${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...options.headers },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = body.detail;
    const message = typeof detail === "string" ? detail : Array.isArray(detail)
      ? detail.map((entry) => entry.msg).join(" / ") : "요청을 처리하지 못했습니다.";
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return body;
}

function Metric({ label, value, hint, tone }) {
  return (
    <div className={`ccd-metric ${tone ? `ccd-metric-${tone}` : ""}`}>
      <span>{label}</span>
      <strong>{number(value)}</strong>
      {hint && <small>{hint}</small>}
    </div>
  );
}

function TimeField({ label, value, onChange, disabled }) {
  return (
    <label className="ccd-time-field">
      <span>{label}</span>
      <input type="time" step="60" value={value || ""} disabled={disabled}
        onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function SmsWindow({ title, description, enabled, start, end, onToggle, onStart, onEnd, disabled }) {
  return (
    <div className="ccd-sms-window">
      <div className="ccd-sms-window-head">
        <div><strong>{title}</strong><p>{enabled ? description : "OFF 기간의 문자는 저장 후 소급 발송하지 않음"}</p></div>
        <button type="button" className={`ccd-toggle ${enabled ? "is-on" : ""}`}
          disabled={disabled} aria-pressed={enabled} onClick={() => onToggle(!enabled)}>
          <span className="ccd-toggle-track"><span /></span>
          {enabled ? "ON" : "OFF"}
        </button>
      </div>
      <div className="ccd-time-fields">
        <TimeField label="발송 시작" value={start} onChange={onStart} disabled={disabled} />
        <TimeField label="발송 종료" value={end} onChange={onEnd} disabled={disabled} />
      </div>
    </div>
  );
}

export default function ConciergeDashboard() {
  const { me } = useAuth();
  const canEdit = me?.role === "SUPER" || me?.role === "MANAGER";
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboard, setDashboard] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [audits, setAudits] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const dirtyRef = useRef(false);
  const requestRef = useRef(null);

  async function reload({ initial = false, afterSave = false } = {}) {
    if (requestRef.current) {
      if (!afterSave) return requestRef.current;
      await requestRef.current;
    }
    const request = api("");
    requestRef.current = request;
    try {
      const data = await request;
      setDashboard(data);
      if (!dirtyRef.current) setDraft(draftFrom(data.settings));
      setError("");
    } catch (cause) {
      setError(cause.message);
    } finally {
      if (requestRef.current === request) requestRef.current = null;
      if (initial) setLoading(false);
    }
  }

  useEffect(() => {
    reload({ initial: true });
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") reload();
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!confirmation) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busy) setConfirmation(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [confirmation, busy]);

  function updateDraft(key, value) {
    dirtyRef.current = true;
    setDraft((current) => ({ ...current, [key]: value }));
    setNotice("");
  }

  function updateStartFromSlider(value) {
    const start = Number(value);
    const end = minutes(draft.upload_end_time) + draft.upload_end_day_offset * 1440;
    updateDraft("upload_start_time", clock(start));
    if (end <= start) {
      setDraft((current) => ({ ...current, upload_end_time: clock(start + 30), upload_end_day_offset: 0 }));
    }
  }

  function updateEndFromSlider(value) {
    const end = Number(value);
    dirtyRef.current = true;
    setDraft((current) => ({ ...current,
      upload_end_time: clock(end), upload_end_day_offset: end >= 1440 ? 1 : 0,
    }));
    setNotice("");
  }

  async function openPreview(changes, kind) {
    if (!Object.keys(changes).length) {
      setNotice("변경된 설정이 없습니다.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const preview = await api("/settings/preview", {
        method: "POST", body: JSON.stringify(changes),
      });
      setConfirmation({ changes, kind, preview });
    } catch (cause) {
      setError(cause.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveChanges() {
    if (!confirmation || confirmation.preview.blocked) return;
    setBusy(true);
    setError("");
    try {
      const saved = await api("/settings", {
        method: "PUT",
        body: JSON.stringify({ ...confirmation.changes,
          expected_revision: confirmation.preview.before.revision }),
      });
      if (confirmation.kind !== "toggle") dirtyRef.current = false;
      setDashboard((current) => current ? { ...current, settings: saved.settings } : current);
      setConfirmation(null);
      setNotice("운영 설정이 저장되었습니다.");
      if (auditOpen) setAudits(null);
      await reload({ afterSave: true });
    } catch (cause) {
      setConfirmation(null);
      setError(cause.status === 409 ? `${cause.message} 현황을 새로고침한 뒤 다시 확인해 주세요.` : cause.message);
      if (cause.status === 409) await reload({ afterSave: true });
    } finally {
      setBusy(false);
    }
  }

  async function showAudits() {
    if (auditOpen) { setAuditOpen(false); return; }
    setAuditOpen(true);
    if (audits) return;
    try {
      const result = await api("/settings/audits?limit=5");
      setAudits(result.items || []);
    } catch (cause) {
      setError(cause.message);
    }
  }

  function openStore(userId) {
    if (!userId) return;
    const returnTo = `${location.pathname}${location.search}`;
    navigate(`/concierge/${userId}?return_to=${encodeURIComponent(returnTo)}`, {
      state: { returnTo, returnSource: "dashboard" },
    });
  }

  if (loading) return <div className="ccd-page ccd-loading">컨시어지 현황을 불러오는 중...</div>;
  if (!dashboard) return (
    <div className="ccd-page ccd-loading" role="alert">
      <p>{error || "현황을 불러오지 못했습니다."}</p>
      <button type="button" className="ccd-button" onClick={() => reload({ initial: true })}>다시 시도</button>
    </div>
  );

  const { settings, operation, current_week: week, next_week: nextWeek,
    approval_capacity: capacity, accounts = [], recent_errors: recentErrors = [], error_statistics: errorStats = {} } = dashboard;
  const endAbsolute = minutes(draft.upload_end_time) + draft.upload_end_day_offset * 1440;
  const startAbsolute = minutes(draft.upload_start_time);
  const scheduleMinutes = endAbsolute - startAbsolute;
  const invalidTime = [...TIME_FIELDS].some((key) => !validClock(draft[key]));
  const invalidSchedule = invalidTime || scheduleMinutes <= 0 || scheduleMinutes > 1440;
  const invalidSms = invalidTime || minutes(draft.upload_completion_sms_start_time) >= minutes(draft.upload_completion_sms_end_time)
    || minutes(draft.view_threshold_sms_start_time) >= minutes(draft.view_threshold_sms_end_time);
  const changes = changedFields(draft, settings);
  const hasChanges = Object.keys(changes).length > 0;
  const state = operation.state || "WAITING";
  const scheduler = operation.scheduler || {};
  const stalledJob = operation.stalled_job || {};
  const backlog = operation.queue_backlog || {};
  const schedulerWarning = scheduler.status === "CONFIG_MISMATCH"
    ? "현황판 자동업로드는 ON이지만 서버 스케줄러 설정은 OFF입니다. 서버 설정과 실행 상태를 확인해 주세요."
    : scheduler.status === "NO_RESPONSE"
      ? `스케줄러 응답이 ${Math.ceil((scheduler.timeout_seconds || 180) / 60)}분 이상 없습니다. 서버 또는 스케줄러 프로세스를 확인해 주세요.`
      : "";

  return (
    <div className="ccd-page">
      <header className="ccd-header">
        <div>
          <span className="ccd-eyebrow">CONCIERGE / OPERATIONS</span>
          <h1>컨시어지 현황판</h1>
          <p>업로드 운영 현황과 다음 일정, 문자 발송 시간을 한곳에서 확인</p>
        </div>
        <div className="ccd-header-actions">
          <span>조회 기준 {kstTime(dashboard.as_of_kst)} KST</span>
          <button type="button" className="ccd-button" disabled={busy} onClick={() => reload()}>새로고침</button>
        </div>
      </header>

      {error && <div className="ccd-alert ccd-alert-error" role="alert">{error}</div>}
      {notice && <div className="ccd-alert ccd-alert-success" role="status">{notice}</div>}

      <section className="ccd-operation ccd-card" aria-label="현재 운영 상태">
        <div className="ccd-operation-top">
          <div>
            <span className="ccd-section-kicker">현재 운영 상태</span>
            <div className="ccd-operation-state"><span className={`ccd-state-dot ccd-state-${state.toLowerCase()}`} />
              <strong>{STATE_LABELS[state] || state}</strong>
              <span>{settings.auto_upload_enabled ? "자동업로드 ON" : "자동업로드 OFF"}</span>
            </div>
          </div>
          <button type="button" className={`ccd-toggle ${settings.auto_upload_enabled ? "is-on" : ""}`}
            disabled={!canEdit || busy} aria-pressed={settings.auto_upload_enabled}
            title={!canEdit ? "설정 변경 권한이 없습니다." : undefined}
            onClick={() => openPreview({ auto_upload_enabled: !settings.auto_upload_enabled }, "toggle")}>
            <span className="ccd-toggle-track"><span /></span>
            자동업로드 {settings.auto_upload_enabled ? "끄기" : "켜기"}
          </button>
        </div>
        {schedulerWarning && <div className="ccd-alert ccd-alert-warning" role="alert">{schedulerWarning}</div>}
        {stalledJob.detected && (
          <div className="ccd-alert ccd-alert-error" role="alert">
            처리 중 작업의 응답이 멈췄습니다. {stalledJob.stage ? `중단 단계: ${stalledJob.stage}. ` : ""}
            마지막 정상 완료 {kstTime(stalledJob.last_normal_finished_at_kst)} · 최대 {stalledJob.auto_recovery?.stale_after_minutes || 30}분 후 게시 여부 확인 및 자동 복구 대상
          </div>
        )}
        <div className="ccd-operation-grid">
          <div><span>현재 처리 중</span><strong>{operation.active_jobs?.length ? `${operation.active_jobs.length}건` : "없음"}</strong>
            {operation.active_jobs?.map((job) => (
              <p key={`${job.user_id}-${job.started_at_kst}`}>{job.store_name || `매장 ${job.user_id}`} · {job.account_name || "계정 배정 전"} · {JOB_LABELS[job.job_type] || job.job_type || "업로드"}{job.stage ? ` / ${job.stage}` : ""}</p>
            ))}</div>
          <div><span>스케줄러 실제 상태</span>
            <strong className={`ccd-health ccd-health-${String(scheduler.status || "unknown").toLowerCase()}`}>
              {SCHEDULER_STATUS_LABELS[scheduler.status] || "확인 중"}
            </strong>
            <p>마지막 응답 {kstTime(scheduler.last_tick_at_kst)}</p></div>
          <div><span>마지막 실행</span><strong>{kstTime(operation.last_started_at_kst)}</strong>
            <p>완료 {kstTime(operation.last_finished_at_kst)}</p></div>
          <div><span>다음 실행 예상</span><strong>{kstTime(operation.next_run_estimate_kst)}</strong>
            <p>대기 작업 및 운영시간 기준</p></div>
        </div>
        <div className="ccd-backlog">
          <div className="ccd-backlog-heading">
            <strong>실제 대기열</strong>
            <span>최소 {backlog.estimate_basis_interval_minutes || settings.min_upload_interval_minutes}분 시작 간격 기준</span>
          </div>
          <div className="ccd-backlog-grid">
            <div><span>수동 대기</span><strong>{number(backlog.manual_pending)}건</strong>
              <small>{backlog.manual_processing ? `진행 중 ${number(backlog.manual_processing)}건` : "접수 순서대로 우선 처리"}</small></div>
            <div><span>재시도 대기</span><strong>{number(backlog.retry_pending)}건</strong>
              <small>실패 후 재실행 예정</small></div>
            <div><span>자동 대기</span><strong>{number(backlog.automatic_pending)}건</strong>
              <small>{backlog.automatic_processing ? `진행 중 ${number(backlog.automatic_processing)}건` : "주간·신규 승인 큐"}</small></div>
            <div><span>우선 작업 추가 지연</span><strong>{durationMinutes(backlog.estimated_priority_delay_minutes)}</strong>
              <small>수동·재시도 대기량 기준 보수적 예상</small></div>
          </div>
        </div>
        <p className="ccd-subnote">자동업로드 OFF 시 진행 중인 작업은 완료 후 중지. 기존 큐는 유지, 수동업로드는 계속 가능.</p>
      </section>

      <section className="ccd-card" aria-label="주간 업로드 현황">
        <div className="ccd-section-title"><div><span className="ccd-section-kicker">WEEKLY FLOW</span>
          <h2>이번 주 업로드</h2><p>{week.week_start_date} 시작 · 전체 및 계정별 현황</p></div>
          <div className="ccd-capacity-pill">남은 시간 수용 가능 <strong>{number(week.remaining_capacity_estimate)}건</strong></div>
        </div>
        <div className="ccd-metrics">
          <Metric label="예정" value={week.planned} />
          <Metric label="성공" value={week.success} tone="success" />
          <Metric label="진행 중" value={week.processing} />
          <Metric label="대기" value={week.pending} />
          <Metric label="재시도" value={week.retry} tone="warning" />
          <Metric label="실패·확인 필요" value={week.failed} tone="danger" />
        </div>
        <p className="ccd-subnote">매장당 현재 상태 1개만 집계. 이번 주 자동업로드 성공 확인 시 성공으로 유지하며, 수동업로드는 제외.</p>
        <div className="ccd-progress-heading"><span>주간 완료율</span><strong>{week.planned ? Math.round(week.success / week.planned * 100) : 0}%</strong></div>
        <div className="ccd-progress"><span style={{ width: `${week.planned ? Math.min(100, week.success / week.planned * 100) : 0}%` }} /></div>
        <div className="ccd-week-footer">
          <span>남은 건수 <strong>{number(week.remaining)}</strong>건 <small>성공하지 않은 건, 위 상태 건수와 중복</small></span>
          <span>승인 대기 <strong>{number(dashboard.approval_waiting_count)}</strong>건</span>
          <span>부족 예상 <strong className={week.shortfall_estimate > 0 ? "ccd-danger" : ""}>{number(week.shortfall_estimate)}</strong>건</span>
        </div>
        {week.shortfall_estimate > 0 && <div className="ccd-alert ccd-alert-warning">현재 일정과 남은 시간으로는 이번 주 업로드 {number(week.shortfall_estimate)}건이 부족할 수 있습니다. 운영시간 확대를 검토해 주세요.</div>}
      </section>

      <div className="ccd-pair">
        <section className="ccd-card ccd-next-week">
          <span className="ccd-section-kicker">NEXT WEEK</span><h2>다음 주 예상</h2>
          <strong>{number(nextWeek.estimated_planned)}<small>건</small></strong>
          <p>{nextWeek.week_start_date} 시작 · 현재 승인·종료일 기준 예상치</p>
          <div className="ccd-next-meta">현재 승인 {number(dashboard.store_counts?.APPROVED)} · 일시정지 {number(dashboard.store_counts?.PAUSED)} · 종료 {number(dashboard.store_counts?.STOPPED)}</div>
        </section>
        <section className="ccd-card ccd-capacity">
          <span className="ccd-section-kicker">SCHEDULE CAPACITY</span><h2>설정 기준 수용량</h2>
          <strong>{number(capacity.approval_limit)}<small>건 / 승인 한도</small></strong>
          <p>계정 {number(capacity.active_account_count)}개 · 하루 {number(capacity.daily_schedule_slots)}회 시작 슬롯 · 최소 {number(capacity.interval_minutes)}분 간격</p>
          <div className="ccd-next-meta">수동업로드·재시도·작업 지연은 예상 수용량에서 제외</div>
        </section>
      </div>

      <section className="ccd-card" aria-label="계정별 현황">
        <div className="ccd-section-title"><div><span className="ccd-section-kicker">ACCOUNTS</span><h2>계정별 현황</h2>
          <p>메인 우선 배정 · 남은 계정 배정 건수는 실행 시점 추정</p></div></div>
        <div className="ccd-table-scroll"><table className="ccd-table"><thead><tr>
          <th>계정</th><th>설정 한도</th><th>이번 주 예정</th><th>성공</th><th>실패</th><th>남은 예상</th><th>남은 시간 수용</th><th>다음 주 예상</th><th>Meta API 쿼터</th><th>미디어 보호 대기</th><th>마지막 업로드</th>
        </tr></thead><tbody>{accounts.map((account) => (
          <tr key={account.account_name}>
            <td><strong>{account.display_name || account.account_name}</strong><small>{account.account_name}</small></td>
            <td>일 {number(account.daily_publish_limit)} / 주 {number(account.weekly_configured_limit)}</td>
            <td>{number(account.current_week_estimated_planned)}</td><td>{number(account.current_week_success)}</td>
            <td>{number(account.current_week_failed)}</td><td>{number(account.current_week_estimated_remaining)}</td>
            <td>{number(account.remaining_week_capacity_estimate)}</td><td>{number(account.next_week_estimated_planned)}</td>
            <td>{account.quota?.status === "ok" ? `${number(account.quota.used)} / ${number(account.quota.limit)} (잔여 ${number(account.quota.remaining)})` : "조회 불가"}</td>
            <td>{account.protection?.state === "WAITING" ? <>
              <strong className="ccd-danger">24시간 보호 대기</strong>
              <small>{kstTime(account.protection.until_kst)}까지 (KST)</small>
              <small>남은 시간 {durationMinutes(Math.ceil(account.protection.remaining_seconds / 60))}</small>
            </> : account.protection?.state === "NONE" ? "대기 없음" : "확인 전"}</td>
            <td>{kstTime(account.last_uploaded_at_kst)}</td>
          </tr>
        ))}</tbody></table></div>
        {dashboard.unassigned_current_week_estimate > 0 && <p className="ccd-danger ccd-table-note">이번 주 계정 배정 가능량 초과 예상: {number(dashboard.unassigned_current_week_estimate)}건</p>}
      </section>

      <section className="ccd-card" aria-label="운영 설정">
        <div className="ccd-section-title"><div><span className="ccd-section-kicker">SETTINGS</span><h2>업로드 일정 및 문자 시간</h2>
          <p>운영 요일 월~일 고정 · 현재 설정은 변경 확인 후 저장</p></div><span className="ccd-revision">설정 v{settings.revision}</span></div>
        <div className="ccd-settings-grid">
          <div className="ccd-schedule">
            <div className="ccd-subheading"><strong>자동업로드 운영시간</strong><span>시작 시각부터 다음 작업 시작까지 최소 {draft.min_upload_interval_minutes}분</span></div>
            <div className="ccd-days">{["월", "화", "수", "목", "금", "토", "일"].map((day) => <span key={day}>{day}</span>)}</div>
            <div className="ccd-range-summary"><strong>{draft.upload_start_time} → {draft.upload_end_day_offset ? "익일 " : ""}{draft.upload_end_time}</strong>
              <span>{invalidSchedule ? "운영시간 확인 필요" : `하루 ${Math.floor(scheduleMinutes / 60)}시간 ${scheduleMinutes % 60}분`}</span></div>
            <div className="ccd-range-row"><label htmlFor="ccd-upload-start">시작 드래그</label>
              <input id="ccd-upload-start" type="range" min="0" max={Math.max(0, Math.min(1410, endAbsolute - 30))} step="30"
                value={startAbsolute} disabled={!canEdit || busy} onChange={(event) => updateStartFromSlider(event.target.value)} /></div>
            <div className="ccd-range-row"><label htmlFor="ccd-upload-end">종료 드래그</label>
              <input id="ccd-upload-end" type="range" min={Math.min(2850, startAbsolute + 30)} max={Math.min(2850, startAbsolute + 1440)} step="30"
                value={endAbsolute} disabled={!canEdit || busy} onChange={(event) => updateEndFromSlider(event.target.value)} /></div>
            <div className="ccd-time-fields">
              <TimeField label="시작 시각" value={draft.upload_start_time} disabled={!canEdit || busy} onChange={(value) => updateDraft("upload_start_time", value)} />
              <TimeField label="종료 시각" value={draft.upload_end_time} disabled={!canEdit || busy} onChange={(value) => updateDraft("upload_end_time", value)} />
              <label className="ccd-time-field"><span>종료 기준일</span><select disabled={!canEdit || busy}
                value={draft.upload_end_day_offset} onChange={(event) => updateDraft("upload_end_day_offset", Number(event.target.value))}>
                <option value={0}>당일</option><option value={1}>익일</option>
              </select></label>
            </div>
            <p className="ccd-subnote">일정 축소 시 승인·일시정지 매장이 새 한도를 초과하면 저장 불가. 주중 변경은 미실행 건부터 적용.</p>
          </div>
          <div className="ccd-sms-settings">
            <SmsWindow title="업로드 완료 문자" description="설정 시각 이후 완료 건은 다음 날 발송"
              enabled={draft.upload_completion_sms_enabled}
              start={draft.upload_completion_sms_start_time} end={draft.upload_completion_sms_end_time}
              onToggle={(value) => updateDraft("upload_completion_sms_enabled", value)}
              onStart={(value) => updateDraft("upload_completion_sms_start_time", value)}
              onEnd={(value) => updateDraft("upload_completion_sms_end_time", value)} disabled={!canEdit || busy} />
            <SmsWindow title="조회수 달성 문자" description="조회수 단계 달성 문자를 설정된 시간대에 발송"
              enabled={draft.view_threshold_sms_enabled}
              start={draft.view_threshold_sms_start_time} end={draft.view_threshold_sms_end_time}
              onToggle={(value) => updateDraft("view_threshold_sms_enabled", value)}
              onStart={(value) => updateDraft("view_threshold_sms_start_time", value)}
              onEnd={(value) => updateDraft("view_threshold_sms_end_time", value)} disabled={!canEdit || busy} />
          </div>
        </div>
        <div className="ccd-settings-actions"><span>{!canEdit ? "설정 변경은 최고·중간 관리자만 가능" : hasChanges ? "저장 전 변경 영향 확인 필요" : `마지막 변경 ${kstTime(settings.updated_at_kst)}`}</span>
          {canEdit && <div><button type="button" className="ccd-button" disabled={!hasChanges || busy} onClick={() => {
            dirtyRef.current = false; setDraft(draftFrom(settings)); setNotice("");
          }}>변경 취소</button>
          <button type="button" className="ccd-button ccd-button-primary" disabled={!hasChanges || busy || invalidSchedule || invalidSms}
            onClick={() => openPreview(changes, "settings")}>변경 결과 확인</button></div>}</div>
        {(invalidSchedule || invalidSms) && <p className="ccd-danger ccd-validation">운영시간은 0~24시간, 문자 발송 종료 시각은 시작 시각 이후로 설정해 주세요.</p>}
        <div className="ccd-audit"><button type="button" onClick={showAudits}>{auditOpen ? "설정 변경 내역 접기" : "최근 설정 변경 내역 보기"}</button>
          {auditOpen && (audits ? audits.length ? <ul>{audits.map((row) => (
            <li key={row.audit_id}><strong>관리자 #{row.changed_by}</strong><span>{kstTime(row.created_at_kst)} KST</span>
              <small>{auditChanges(row).join(" · ") || "설정 변경"}</small></li>
          ))}</ul> : <p>변경 내역 없음</p> : <p>불러오는 중...</p>)}</div>
      </section>

      <section className="ccd-card" aria-label="최근 업로드 오류"><div className="ccd-section-title"><div>
        <span className="ccd-section-kicker">RECENT ISSUES</span><h2>최근 업로드 오류</h2><p>재시도 대기 포함 · 최근 오류 5건</p></div></div>
        <p className="ccd-subnote">이번 주 실패 시도 누적 <strong>{number(errorStats.failed_attempts)}</strong>회 · 실패 이력 매장 <strong>{number(errorStats.stores_with_failed_attempts)}</strong>곳 (수동 포함, 재시도 후 성공한 기록도 포함. 주간 매장 상태와 별도 집계)</p>
        {recentErrors.length ? <div className="ccd-errors">{recentErrors.map((item, index) => (
          <div className="ccd-error-item" key={`${item.user_id}-${item.occurred_at_kst}-${index}`}>
            <div><strong>{item.store_name || `매장 ${item.user_id || "-"}`}</strong><span>{item.source_label || item.source_type || "업로드"}{item.instagram_account ? ` · ${item.instagram_account}` : ""} · {kstTime(item.occurred_at_kst)}</span>
              {item.failure_stage && <span>실패 단계: {item.failure_stage}</span>}
              <p>{item.summary || "오류 내용 없음"}</p></div>
            {item.user_id && <button type="button" className="ccd-button" onClick={() => openStore(item.user_id)}>매장 상세</button>}
          </div>
        ))}</div> : <div className="ccd-empty">최근 업로드 오류 없음</div>}
      </section>

      {confirmation && <div className="ccd-modal-backdrop" onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) setConfirmation(null);
      }}><div className="ccd-modal" role="dialog" aria-modal="true" aria-labelledby="ccd-confirm-title">
        <span className="ccd-section-kicker">CHANGE PREVIEW</span><h2 id="ccd-confirm-title">{confirmation.kind === "toggle" ? "자동업로드 상태 변경" : "운영 설정 변경 확인"}</h2>
        <p>{confirmation.kind === "toggle" && !confirmation.preview.after.auto_upload_enabled
          ? "진행 중인 업로드는 완료한 뒤 신규 자동업로드를 중지합니다. 수동업로드는 계속 가능합니다."
          : "변경 전후 수용량과 이번 주 남은 작업을 확인한 뒤 저장해 주세요."}</p>
        <div className="ccd-modal-compare"><div><span>변경 전</span><strong>{confirmation.preview.before.upload_start_time} ~ {confirmation.preview.before.upload_end_day_offset ? "익일 " : ""}{confirmation.preview.before.upload_end_time}</strong>
          <small>승인 한도 {number(confirmation.preview.before_capacity.approval_limit)}건 · 자동업로드 {confirmation.preview.before.auto_upload_enabled ? "ON" : "OFF"}</small></div>
          <div><span>변경 후</span><strong>{confirmation.preview.after.upload_start_time} ~ {confirmation.preview.after.upload_end_day_offset ? "익일 " : ""}{confirmation.preview.after.upload_end_time}</strong>
            <small>승인 한도 {number(confirmation.preview.after_capacity.approval_limit)}건 · 자동업로드 {confirmation.preview.after.auto_upload_enabled ? "ON" : "OFF"}</small></div></div>
        <div className="ccd-modal-changes"><strong>변경 항목</strong>{Object.keys(confirmation.changes).map((key) => (
          <div key={key}><span>{SETTING_LABELS[key] || key}</span>
            <span>{settingValue(key, confirmation.preview.before[key])} → <b>{settingValue(key, confirmation.preview.after[key])}</b></span></div>
        ))}</div>
        <div className="ccd-modal-numbers"><span>현재 승인·일시정지 <strong>{number(confirmation.preview.occupied_approval_slots)}건</strong></span>
          <span>이번 주 남음 <strong>{number(confirmation.preview.current_week_remaining_targets)}건</strong></span>
          <span>남은 시간 부족 예상 <strong className={confirmation.preview.current_week_shortfall_estimate ? "ccd-danger" : ""}>{number(confirmation.preview.current_week_shortfall_estimate)}건</strong></span></div>
        {confirmation.preview.warnings?.map((warning) => <p className="ccd-modal-warning" key={warning}>{warning}</p>)}
        {confirmation.preview.blocked && <div className="ccd-alert ccd-alert-error">현재 승인·일시정지 매장을 수용하지 못하는 일정으로 변경할 수 없습니다.</div>}
        <div className="ccd-modal-actions"><button type="button" className="ccd-button" disabled={busy} onClick={() => setConfirmation(null)}>취소</button>
          <button type="button" className="ccd-button ccd-button-primary" disabled={busy || confirmation.preview.blocked} onClick={saveChanges}>{busy ? "저장 중..." : "확인 후 저장"}</button></div>
      </div></div>}
    </div>
  );
}
